// One download and one decoded copy per document, shared by every page
// thumbnail that shows it, freed a little after the last one unmounts.
//
// Thumbnails try the stored page image first (DocPageThumb); this cache is
// the fallback for pages without one. Before it, each thumbnail fetched and
// parsed the whole PDF on its own and never freed it.

import { api } from './api.js';

// How long a document stays decoded after its last thumbnail goes away, so
// scrolling it back on screen doesn't download it again.
const GRACE_MS = 30_000;

/**
 * acquire(docId) → Promise<entry>; release(docId) when done with it. Each
 * acquire needs exactly one release. `load(docId)` returns an object with a
 * destroy() method; a failed load is not cached.
 */
export function createDocFileCache({ load, setTimer = setTimeout, clearTimer = clearTimeout, graceMs = GRACE_MS }) {
  const entries = new Map(); // docId -> { promise, refs, timer }

  function acquire(docId) {
    let entry = entries.get(docId);
    if (!entry) {
      entry = { refs: 0, timer: null, promise: null };
      entry.promise = load(docId).catch((err) => {
        if (entries.get(docId) === entry) entries.delete(docId);
        throw err;
      });
      entries.set(docId, entry);
    }
    if (entry.timer != null) {
      clearTimer(entry.timer);
      entry.timer = null;
    }
    entry.refs += 1;
    return entry.promise;
  }

  function release(docId) {
    const entry = entries.get(docId);
    if (!entry) return;
    entry.refs = Math.max(0, entry.refs - 1);
    if (entry.refs > 0 || entry.timer != null) return;
    entry.timer = setTimer(() => {
      if (entries.get(docId) !== entry || entry.refs > 0) return;
      entries.delete(docId);
      entry.promise.then((loaded) => loaded?.destroy?.(), () => {});
    }, graceMs);
  }

  return { acquire, release };
}

// The real loader: fetch the file once, then either an object URL for an
// image document (a scanned sheet) or a pdf.js document for a PDF.
async function loadDocFile(docId) {
  const res = await fetch(api.documentFileUrl(docId));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const type = (res.headers.get('Content-Type') || '').toLowerCase();
  if (type.startsWith('image/')) {
    const url = URL.createObjectURL(await res.blob());
    return { kind: 'image', url, destroy: () => URL.revokeObjectURL(url) };
  }
  const data = await res.arrayBuffer();
  const [pdfjs, workerMod] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default;
  const pdf = await pdfjs.getDocument({ data }).promise;
  return { kind: 'pdf', pdf, destroy: () => pdf.destroy?.() };
}

export const docFiles = createDocFileCache({ load: loadDocFile });
